import { useState, useEffect, useContext } from 'react'
import moment from 'moment-timezone'

import LoadingContext from '../../context/Loading/Loading'
import UserContext from '../../context/User/User'
import AuthService from '../../utilities/services/auth.service'

import {
  formatCurrency,
  formatNumber,
  formatDateTime,
  getTimezoneDate,
  capitalizeString,
  createWorkbook,
  saveWorkbook,
  DEFAULTNUMHEADERS,
  setColumnsAndRows,
  setHeaders,
  currencyFormat,
  numberFormat,
  addWorksheet,
  styleTotalRow,
  excelHeaderStyle,
  formatString,
  getEventNameForOrder,
  getEventDateForOrder,
  EMPTY_DISPLAY_VALUE,
  checkPermission,
} from '../../utilities/helpers'

import {
  getPaymentListingsV4,
  getBoxOfficeAccountReps,
} from '../../utilities/api'
import { useReportRefresh } from '../../hooks/useReportRefresh'

import {
  dateTime,
  constants,
  isClubSeatLicenseFeeReportEnabled,
  ORGANIZATION_PERMISSION_SLUGS,
} from '@blocktickets/shared-utils'

import Form from 'react-bootstrap/Form'
import Card from 'react-bootstrap/Card'
import Table from 'react-bootstrap/Table'
import Tabs from 'react-bootstrap/Tabs'
import Tab from 'react-bootstrap/Tab'

import { ReportActions } from '../ReportActions'
import { DateInputWrapper } from '../DateInputWrapper'
import { EmptyContainer } from '../EmptyContainer'
import { LoadingContainer } from '../LoadingContainer'
import { PageLoadingContainer } from '../PageLoadingContainer'
import { NoPermissionsContainer } from '../NoPermissionsContainer'
import { getFacilityFee } from './getFacilityFee'
import {
  buildDailySettlementDataFromListings,
  isCompleteOrder,
  isRefundedOrder,
  getSettlementNet,
  getSettlementProcessingFee,
  getSettlementClubSeatLicenseFees,
  getSettlementOtherAdditionalFees,
  getDailySettlementStatusLabel,
  getDailySettlementRefundAmount,
  getSettlementOrderTimestamp,
  hasPartialRefund,
} from './dailySettlementData'
import {
  buildDailySettlementQueryParams,
  formatDailySettlementReportDateLabel,
  isDailySettlementDateRangeInvalid,
} from './dailySettlementDateRange'

const { PRODUCT_TYPE_LABEL, ORDER_STATUS_LABEL } = constants

const formatPaymentMethodLabel = (paymentMethod) =>
  paymentMethod ? formatString(paymentMethod) : '—'

export default function DailySettlementWrapper({ organization }) {
  const { isLoading, showLoading, hideLoading } = useContext(LoadingContext)
  const { orgPermissions } = useContext(UserContext)
  const { getPermissions } = AuthService

  const showClubSeatLicenseFees = isClubSeatLicenseFeeReportEnabled(
    organization?.uuid
  )

  const orgPermissionsReady = orgPermissions?.length > 0
  const hasPermission =
    orgPermissionsReady &&
    checkPermission(
      getPermissions(),
      ORGANIZATION_PERMISSION_SLUGS.VIEW_DAILY_SETTLEMENT_REPORT
    )

  const [isLoadingData, setIsLoadingData] = useState(false)
  const { refreshToken, refresh, completeRefresh } =
    useReportRefresh(isLoadingData)

  const [data, setData] = useState(null)

  const [fromDate, setFromDate] = useState(null)
  const [toDate, setToDate] = useState(null)

  const [account, setAccount] = useState('all')

  const [accounts, setAccounts] = useState([
    { id: 'all', label: 'All orders', value: 'all' },
  ])

  // Timezone used for the date filter; we format date_paid in this TZ so the displayed date matches the selected day
  const [displayTimezone, setDisplayTimezone] = useState(
    () =>
      (typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat?.().resolvedOptions?.().timeZone) ||
      'UTC'
  )

  useEffect(() => {
    if (!orgPermissionsReady) return
    if (!hasPermission) {
      hideLoading()
      return
    }
    if (!organization?.uuid) return
    showLoading()
    getBoxOfficeAccountReps(organization.uuid)
      .then((res) => {
        const accountReps = res?.data ?? []
        setAccounts([
          { id: 'all', label: 'All orders', value: 'all' },
          ...accountReps,
        ])
      })
      .catch((err) => {
        console.error('Failed to fetch account reps:', err)
      })
      .finally(() => {
        hideLoading()
      })
  }, [organization?.uuid, orgPermissionsReady, hasPermission])

  const isDateRangeInvalid = isDailySettlementDateRangeInvalid(fromDate, toDate)

  useEffect(() => {
    const fetchData = async () => {
      if (!fromDate || isDateRangeInvalid) return
      setIsLoadingData(true)

      try {
        const tz = dateTime.getBrowserTimezone('UTC')
        setDisplayTimezone(tz)
        const { fromUtcIso, toUtcIso, options } =
          buildDailySettlementQueryParams(fromDate, toDate, tz)

        const accountValue = account

        // Args: …, buyerType, boxOfficeAgent, options — account filter is boxOfficeAgent, not buyerType
        const res = await getPaymentListingsV4(
          organization.uuid,
          fromUtcIso,
          toUtcIso,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          undefined,
          accountValue !== 'all' ? accountValue : undefined,
          options
        )

        const { raw, byPaymentMethod } = buildDailySettlementDataFromListings(
          res?.data || []
        )

        setData({
          raw,
          byPaymentMethod,
        })
      } catch (error) {
        console.error('Error fetching daily settlements:', error)
        setData(null)
      } finally {
        setIsLoadingData(false)
        completeRefresh()
      }
    }
    if (!orgPermissionsReady || !hasPermission) return
    fetchData()
  }, [
    fromDate,
    toDate,
    isDateRangeInvalid,
    organization?.uuid,
    account,
    orgPermissionsReady,
    hasPermission,
    refreshToken,
  ])

  const handleExcelExport = async () => {
    try {
      if (!orgPermissionsReady || !hasPermission) return
      if (!data || (!data.byPaymentMethod?.length && !data.raw?.length)) return

      const accountValue = account
      const reportDate = formatDailySettlementReportDateLabel(fromDate, toDate)
      const organizationName = organization?.name ?? ''
      const defaultHeaderLines = reportDate ? [`Date: ${reportDate}`] : []
      const headerLines = [
        ...defaultHeaderLines,
        ...(accountValue && accountValue !== 'all'
          ? [`Account: ${capitalizeString(accountValue)}`]
          : []),
      ]

      const workbook = createWorkbook()
      const byPaymentMethod = data.byPaymentMethod ?? []
      const raw = data.raw ?? []

      // —— Worksheet 1: Summary by Payment Method ——
      const summarySheet = addWorksheet('Summary by Payment Method', workbook)
      setHeaders(
        'Summary by Payment Method',
        organizationName,
        summarySheet,
        headerLines
      )

      const summaryColumns = [
        'payment_method',
        'order_count',
        'quantity',
        'gross',
        'service_fees',
        'facility_fee',
        ...(showClubSeatLicenseFees ? ['club_seat_license_fees'] : []),
        'additional_fees',
        'processing_fees',
        'net',
      ]
      const formatSummaryRow = (row) => ({
        payment_method: formatPaymentMethodLabel(row.paymentMethod),
        order_count: row.orderCount ?? 0,
        quantity: row.ticketsSold ?? 0,
        gross: Number(row.grossSales ?? 0),
        service_fees: Number(row.serviceFees ?? 0),
        facility_fee: Number(row.facilityFees ?? 0),
        ...(showClubSeatLicenseFees
          ? { club_seat_license_fees: Number(row.clubSeatLicenseFees ?? 0) }
          : {}),
        additional_fees: Number(row.additionalFees ?? 0),
        processing_fees: Number(row.processingFees ?? 0),
        net: Number(row.netSales ?? 0),
      })

      const summaryTotals = byPaymentMethod.length
        ? byPaymentMethod.reduce(
            (acc, row) => ({
              paymentMethod: 'Total',
              orderCount: acc.orderCount + (row.orderCount ?? 0),
              ticketsSold: acc.ticketsSold + (row.ticketsSold ?? 0),
              grossSales: acc.grossSales + Number(row.grossSales ?? 0),
              serviceFees: acc.serviceFees + Number(row.serviceFees ?? 0),
              facilityFees: acc.facilityFees + Number(row.facilityFees ?? 0),
              clubSeatLicenseFees:
                acc.clubSeatLicenseFees + Number(row.clubSeatLicenseFees ?? 0),
              additionalFees:
                acc.additionalFees + Number(row.additionalFees ?? 0),
              processingFees:
                acc.processingFees + Number(row.processingFees ?? 0),
              netSales: acc.netSales + Number(row.netSales ?? 0),
            }),
            {
              orderCount: 0,
              ticketsSold: 0,
              grossSales: 0,
              serviceFees: 0,
              facilityFees: 0,
              clubSeatLicenseFees: 0,
              additionalFees: 0,
              processingFees: 0,
              netSales: 0,
            }
          )
        : null

      const summaryRows = summaryTotals
        ? [
            ...byPaymentMethod.map(formatSummaryRow),
            formatSummaryRow(summaryTotals),
          ]
        : byPaymentMethod.map(formatSummaryRow)
      setColumnsAndRows(summaryColumns, summaryRows, (r) => r, summarySheet)
      const summaryHeaderRows = DEFAULTNUMHEADERS + headerLines.length
      numberFormat(2, summarySheet, summaryHeaderRows)
      numberFormat(3, summarySheet, summaryHeaderRows)
      for (let c = 4; c <= summaryColumns.length; c++) {
        currencyFormat(c, summarySheet, summaryHeaderRows)
      }

      const lastRow = summarySheet.lastRow
      styleTotalRow(lastRow, summarySheet, false)

      // —— Worksheet 2: Detailed Transactions ——
      const transactionsSheet = addWorksheet('Detailed Transactions', workbook)
      setHeaders(
        'Detailed Transactions',
        organizationName,
        transactionsSheet,
        headerLines
      )

      const isAllAccounts = accountValue === 'all'
      const transactionColumns = [
        ...(isAllAccounts ? ['sales_rep'] : []),
        'order_date',
        'date_paid',
        'order_id',
        'customer_name',
        'customer_email',
        'event',
        'event_date',
        'product_type',
        'quantity',
        'gross',
        'service_fees',
        'facility_fee',
        'processing_fees',
        ...(showClubSeatLicenseFees ? ['club_seat_license_fees'] : []),
        'additional_fees',
        'net',
        'service_fees_owed',
        'refund',
        'payment_method',
        'status',
      ]
      const formatTransactionRow = (order) => {
        const zonedDate = getTimezoneDate(
          getSettlementOrderTimestamp(order),
          order?.timezone
        )
        const orderDate = zonedDate
          ? formatDateTime(zonedDate)
          : EMPTY_DISPLAY_VALUE
        // Date-only (e.g. "2026-03-03" or Date at UTC midnight) must show as that calendar date; timestamps show in filter TZ
        const tz = displayTimezone || 'UTC'
        const rawPaid = order?.datePaid
        const isDateOnly =
          (typeof rawPaid === 'string' &&
            /^\d{4}-\d{2}-\d{2}$/.test(rawPaid.trim())) ||
          (rawPaid instanceof Date &&
            rawPaid.getUTCHours() === 0 &&
            rawPaid.getUTCMinutes() === 0 &&
            rawPaid.getUTCSeconds() === 0)
        const datePaid = rawPaid
          ? isDateOnly
            ? moment.utc(rawPaid).format('ddd, MMM D, YYYY')
            : moment.utc(rawPaid).tz(tz).format('ddd, MMM D, YYYY')
          : EMPTY_DISPLAY_VALUE
        const productType =
          PRODUCT_TYPE_LABEL[order?.productType] ??
          capitalizeString(order?.productType ?? EMPTY_DISPLAY_VALUE)
        const paymentMethod = formatPaymentMethodLabel(order?.paymentMethod)
        const statusLabel = getDailySettlementStatusLabel(
          order,
          ORDER_STATUS_LABEL?.[order?.status] ??
            capitalizeString(order?.status ?? EMPTY_DISPLAY_VALUE)
        )
        const ref = isRefundedOrder(order)
        const grossVal = Number(order?.gross ?? 0)
        const sfVal = order.serviceFee ?? 0
        const ffVal = getFacilityFee(order)
        const pfVal = getSettlementProcessingFee(order)
        const cslfVal = getSettlementClubSeatLicenseFees(order)
        const afVal = getSettlementOtherAdditionalFees(order)
        const netVal = getSettlementNet(order)
        return {
          ...(isAllAccounts
            ? {
                sales_rep: order?.accountRep
                  ? capitalizeString(order.accountRep)
                  : EMPTY_DISPLAY_VALUE,
              }
            : {}),
          order_date: orderDate,
          date_paid: datePaid,
          order_id: order?.orderId ?? EMPTY_DISPLAY_VALUE,
          customer_name:
            capitalizeString(order?.customerName ?? '').trim() ||
            EMPTY_DISPLAY_VALUE,
          customer_email: order?.customerEmail?.trim() || EMPTY_DISPLAY_VALUE,
          event: getEventNameForOrder(order),
          event_date: getEventDateForOrder(order, order?.timezone),
          product_type: productType,
          quantity: Number(order?.quantity ?? 0),
          gross: ref ? -Math.abs(grossVal) : grossVal,
          service_fees: ref ? -Math.abs(sfVal) : sfVal,
          facility_fee: ref ? -Math.abs(ffVal) : ffVal,
          processing_fees: ref ? -Math.abs(pfVal) : pfVal,
          ...(showClubSeatLicenseFees
            ? { club_seat_license_fees: ref ? -Math.abs(cslfVal) : cslfVal }
            : {}),
          additional_fees: ref ? -Math.abs(afVal) : afVal,
          net: ref ? -Math.abs(netVal) : netVal,
          service_fees_owed: order.serviceFeeOwed ?? 0,
          refund: -Math.abs(getDailySettlementRefundAmount(order)),
          payment_method: paymentMethod,
          status: statusLabel,
        }
      }

      const transactionTotals = raw.length
        ? (() => {
            const sales = raw.reduce(
              (acc, order) => {
                if (!isCompleteOrder(order)) return acc
                return {
                  orderCount: acc.orderCount + 1,
                  quantity: acc.quantity + Number(order?.quantity ?? 0),
                  gross: acc.gross + Number(order?.gross ?? 0),
                  serviceFees: acc.serviceFees + (order.serviceFee ?? 0),
                  facilityFees: acc.facilityFees + getFacilityFee(order),
                  processingFees:
                    acc.processingFees + getSettlementProcessingFee(order),
                  clubSeatLicenseFees:
                    acc.clubSeatLicenseFees +
                    getSettlementClubSeatLicenseFees(order),
                  additionalFees:
                    acc.additionalFees +
                    getSettlementOtherAdditionalFees(order),
                  net: acc.net + getSettlementNet(order),
                  serviceFeesOwed:
                    acc.serviceFeesOwed + (order.serviceFeeOwed ?? 0),
                }
              },
              {
                orderCount: 0,
                quantity: 0,
                gross: 0,
                serviceFees: 0,
                facilityFees: 0,
                processingFees: 0,
                clubSeatLicenseFees: 0,
                additionalFees: 0,
                net: 0,
                serviceFeesOwed: 0,
              }
            )
            const refundNet = raw.reduce(
              (sum, order) => sum + getDailySettlementRefundAmount(order),
              0
            )
            return { ...sales, refundNet }
          })()
        : null

      if (raw.length > 0) {
        const txHeaderRows = DEFAULTNUMHEADERS + headerLines.length

        // Totals section (separate from transactions table - add manually to avoid styleHeaders merge conflict)
        if (transactionTotals) {
          const totalsHeaders = [
            'TOTALS',
            '# of Orders',
            'Quantity',
            'Gross',
            'Service Fees',
            'Facility Fee',
            'Processing Fees',
            ...(showClubSeatLicenseFees ? ['Club Seat License Fees'] : []),
            'Additional Fees',
            'Net',
            'Service Fees Owed',
            'Refund',
          ]
          const refundTotalAbs = Math.abs(transactionTotals.refundNet ?? 0)
          const totalsDataRow = [
            '',
            transactionTotals.orderCount,
            transactionTotals.quantity,
            transactionTotals.gross,
            transactionTotals.serviceFees,
            transactionTotals.facilityFees,
            transactionTotals.processingFees,
            ...(showClubSeatLicenseFees
              ? [transactionTotals.clubSeatLicenseFees]
              : []),
            transactionTotals.additionalFees,
            transactionTotals.net,
            transactionTotals.serviceFeesOwed,
            -refundTotalAbs,
          ]
          const totalsHeaderRowRef = transactionsSheet.addRow(totalsHeaders)
          totalsHeaderRowRef.eachCell((cell) => {
            cell.style = { ...cell.style, ...excelHeaderStyle }
          })
          const totalsDataRowRef = transactionsSheet.addRow(totalsDataRow)
          styleTotalRow(totalsDataRowRef, transactionsSheet, false)
          const grossCol = 4
          const refundTotalsCol = totalsHeaders.length
          numberFormat(2, transactionsSheet, txHeaderRows)
          numberFormat(3, transactionsSheet, txHeaderRows)
          for (let c = grossCol; c <= refundTotalsCol; c++) {
            currencyFormat(c, transactionsSheet, txHeaderRows)
          }
          transactionsSheet.addRow([]) // Blank row before transactions
        }

        // Transactions table
        setColumnsAndRows(
          transactionColumns,
          raw,
          formatTransactionRow,
          transactionsSheet
        )
        const totalsOffset = transactionTotals ? 3 : 0 // totals header + data + blank
        const transactionTableHeaderRow = txHeaderRows + totalsOffset
        const quantityCol = transactionColumns.indexOf('quantity') + 1
        const lastCurrencyCol = transactionColumns.indexOf('refund') + 1
        numberFormat(quantityCol, transactionsSheet, transactionTableHeaderRow)
        for (let c = quantityCol + 1; c <= lastCurrencyCol; c++) {
          currencyFormat(c, transactionsSheet, transactionTableHeaderRow)
        }
      }

      await saveWorkbook(workbook, 'daily settlement')
    } catch (err) {
      console.error('Daily settlement Excel export failed:', err)
    }
  }

  if (!orgPermissionsReady) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  if (!hasPermission) {
    return (
      <div className="spacer-sm">
        <NoPermissionsContainer />
      </div>
    )
  }

  if (isLoading) {
    return <PageLoadingContainer style="without-sidebar" />
  }

  return (
    <section
      className="spacer-sm"
      data-testid="daily-settlement-report-section"
    >
      <header className="section-header-sm">
        <div className="section-heading">
          <h1>Daily Settlement</h1>
        </div>
        <p className="section-header-desc">
          View a complete breakdown of all sales and fees for the selected day
          or date range.
        </p>
      </header>
      <ReportActions
        isLoading={isLoadingData}
        onRefresh={refresh}
        isRefreshing={isLoadingData}
        selected={fromDate}
        downloadReport={handleExcelExport}
        data={
          !fromDate || isDateRangeInvalid
            ? null
            : (data?.raw ?? data?.byPaymentMethod)
        }
      >
        <Form.Group controlId="account" style={{ width: '400px' }}>
          <Form.Label>Filter by</Form.Label>
          <Form.Select
            value={account?.value ?? account ?? 'all'}
            onChange={(e) => setAccount(e.target.value)}
          >
            <option disabled hidden value="" />
            {accounts?.map((acc) => (
              <option key={acc?.id} value={acc?.value}>
                {acc?.value !== 'all'
                  ? capitalizeString(acc?.label)
                  : acc?.label}
              </option>
            ))}
          </Form.Select>
        </Form.Group>
        <div style={{ width: '220px' }}>
          <Form.Group controlId="daily-settlement-start-group">
            <Form.Label htmlFor="daily-settlement-start">Start Date</Form.Label>
            <DateInputWrapper
              id="daily-settlement-start"
              startDate={fromDate}
              selectedDate={fromDate}
              setDate={setFromDate}
              showMinDate={false}
              isReadOnly={isLoadingData}
              size="sm"
              styles="form-control-height"
            />
          </Form.Group>
        </div>
        <div style={{ width: '220px' }}>
          <Form.Group controlId="daily-settlement-end-group">
            <Form.Label htmlFor="daily-settlement-end">End Date</Form.Label>
            <DateInputWrapper
              id="daily-settlement-end"
              startDate={fromDate}
              selectedDate={toDate}
              setDate={setToDate}
              showMinDate={false}
              isReadOnly={isLoadingData || !fromDate}
              size="sm"
              styles="form-control-height"
              error={isDateRangeInvalid}
            />
          </Form.Group>
        </div>
      </ReportActions>
      {isDateRangeInvalid && (
        <Form.Text
          className="text-danger d-block mb-4"
          data-testid="daily-settlement-date-range-error"
        >
          End date cannot be before start date.
        </Form.Text>
      )}
      <Results
        isLoading={isLoadingData}
        data={data}
        fromDate={fromDate}
        toDate={toDate}
        isDateRangeInvalid={isDateRangeInvalid}
        accounts={accounts}
        account={account}
        displayTimezone={displayTimezone}
        showClubSeatLicenseFees={showClubSeatLicenseFees}
      />
    </section>
  )
}

const EmptyRow = ({ colSpan = 16 }) => (
  <tr className="white">
    <td colSpan={colSpan} className="td--empty border-bottom"></td>
  </tr>
)

function DailySettlementByPaymentMethod({
  data,
  account,
  showClubSeatLicenseFees,
}) {
  const byPaymentMethod = data?.byPaymentMethod ?? []
  const hasData = byPaymentMethod.length > 0
  const totals = hasData
    ? byPaymentMethod.reduce(
        (acc, row) => ({
          orderCount: acc.orderCount + row.orderCount,
          ticketsSold: acc.ticketsSold + row.ticketsSold,
          grossSales: acc.grossSales + row.grossSales,
          serviceFees: acc.serviceFees + row.serviceFees,
          facilityFees: acc.facilityFees + Number(row.facilityFees ?? 0),
          clubSeatLicenseFees:
            acc.clubSeatLicenseFees + Number(row.clubSeatLicenseFees ?? 0),
          additionalFees: acc.additionalFees + Number(row.additionalFees ?? 0),
          processingFees: acc.processingFees + row.processingFees,
          netSales: acc.netSales + row.netSales,
        }),
        {
          orderCount: 0,
          ticketsSold: 0,
          grossSales: 0,
          serviceFees: 0,
          facilityFees: 0,
          clubSeatLicenseFees: 0,
          additionalFees: 0,
          processingFees: 0,
          netSales: 0,
        }
      )
    : null

  const TableHeader = () => (
    <thead>
      <tr className="md">
        <th className="uppercase text-body">Payment Method</th>
        <th className="uppercase text-body text-left"># of Orders</th>
        <th className="uppercase text-body text-left">Quantity</th>
        <th className="uppercase text-body text-left">Gross</th>
        <th className="uppercase text-body text-left">Service Fees</th>
        <th className="uppercase text-body text-left">Facility Fee</th>
        {showClubSeatLicenseFees && (
          <th className="uppercase text-body text-left">
            Club Seat License Fees
          </th>
        )}
        <th className="uppercase text-body text-left">Additional Fees</th>
        <th className="uppercase text-body text-left">Processing Fees</th>
        <th className="uppercase text-body text-left">Net</th>
      </tr>
    </thead>
  )

  const TotalRow = () => (
    <tr className="total-row dark md">
      <td className="text-uppercase">Total:</td>
      <td className="whitespace-nowrap text-left">
        {formatNumber(totals?.orderCount ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatNumber(totals?.ticketsSold ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.grossSales ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.serviceFees ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.facilityFees ?? 0)}
      </td>
      {showClubSeatLicenseFees && (
        <td className="whitespace-nowrap text-left">
          {formatCurrency(totals?.clubSeatLicenseFees ?? 0)}
        </td>
      )}
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.additionalFees ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.processingFees ?? 0)}
      </td>
      <td className="whitespace-nowrap text-left">
        {formatCurrency(totals?.netSales ?? 0)}
      </td>
    </tr>
  )

  if (data && !hasData) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>
            No payment methods for this date{' '}
            {account !== 'all' && (
              <span>under the {capitalizeString(account)} account</span>
            )}
          </p>
          <p className="subtitle subtitle--dark">
            There are no payment methods. Please change your filters.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <Card.Title as="h5" className="mb-3">
        Summary by Payment Method
      </Card.Title>
      <div className="table-container border-bottom-0">
        <div className="table-container--fixed-height">
          <Table>
            <TableHeader />
            <tbody>
              {byPaymentMethod.map((row, idx) => (
                <tr key={idx} className="md-x">
                  <td className="whitespace-nowrap">
                    {formatPaymentMethodLabel(row.paymentMethod)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatNumber(row.orderCount)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatNumber(row.ticketsSold)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.grossSales)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.serviceFees)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.facilityFees ?? 0)}
                  </td>
                  {showClubSeatLicenseFees && (
                    <td className="whitespace-nowrap text-left">
                      {formatCurrency(row.clubSeatLicenseFees ?? 0)}
                    </td>
                  )}
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.additionalFees ?? 0)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.processingFees)}
                  </td>
                  <td className="whitespace-nowrap text-left">
                    {formatCurrency(row.netSales)}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <TotalRow />
            </tfoot>
          </Table>
        </div>
      </div>
    </Card>
  )
}

function DailySettlementTransactions({
  account,
  data,
  displayTimezone = 'UTC',
  showClubSeatLicenseFees,
}) {
  const raw = data?.raw ?? []
  const isAllAccounts = account && account === 'all'
  const tz = displayTimezone || 'UTC'
  const metadataColCount = isAllAccounts ? 11 : 10
  const detailColCount = metadataColCount + (showClubSeatLicenseFees ? 11 : 10)

  const formatRefundCell = (order) => {
    const refundAmount = getDailySettlementRefundAmount(order)
    if (refundAmount <= 0) return formatCurrency(0)
    return `-${formatCurrency(refundAmount)}`
  }

  const totals = raw.length
    ? (() => {
        const sales = raw.reduce(
          (acc, order) => {
            if (!isCompleteOrder(order)) return acc
            return {
              orderCount: acc.orderCount + 1,
              quantity: acc.quantity + Number(order?.quantity ?? 0),
              gross: acc.gross + Number(order?.gross ?? 0),
              serviceFees: acc.serviceFees + (order.serviceFee ?? 0),
              facilityFees: acc.facilityFees + getFacilityFee(order),
              processingFees:
                acc.processingFees + getSettlementProcessingFee(order),
              clubSeatLicenseFees:
                acc.clubSeatLicenseFees +
                getSettlementClubSeatLicenseFees(order),
              additionalFees:
                acc.additionalFees + getSettlementOtherAdditionalFees(order),
              net: acc.net + getSettlementNet(order),
              serviceFeesOwed:
                acc.serviceFeesOwed + (order.serviceFeeOwed ?? 0),
            }
          },
          {
            orderCount: 0,
            quantity: 0,
            gross: 0,
            serviceFees: 0,
            facilityFees: 0,
            processingFees: 0,
            clubSeatLicenseFees: 0,
            additionalFees: 0,
            net: 0,
            serviceFeesOwed: 0,
          }
        )
        const refundNet = raw.reduce(
          (sum, order) => sum + getDailySettlementRefundAmount(order),
          0
        )
        return { ...sales, refundNet }
      })()
    : null

  const refundTotalDisplay = Math.abs(totals?.refundNet ?? 0)

  const formatOrderDate = (date, timezone) => {
    const zonedDate = getTimezoneDate(date, timezone)
    return zonedDate ? formatDateTime(zonedDate) : EMPTY_DISPLAY_VALUE
  }

  const SummaryHeader = () => (
    <tr className="md">
      <th className="uppercase text-body" colSpan={metadataColCount}>
        Totals
      </th>
      <th className="uppercase text-body xl"># of Orders</th>
      <th className="uppercase text-body xl">Quantity</th>
      <th className="uppercase text-body xl">Gross</th>
      <th className="uppercase text-body xl">Service Fees</th>
      <th className="uppercase text-body xl">Facility Fee</th>
      <th className="uppercase text-body xl">Processing Fees</th>
      {showClubSeatLicenseFees && (
        <th className="uppercase text-body xl">Club Seat License Fees</th>
      )}
      <th className="uppercase text-body xl">Additional Fees</th>
      <th className="uppercase text-body xl">Net</th>
      <th className="uppercase text-body xl">Service Fees Owed</th>
      <th className="uppercase text-body xl">Refund</th>
    </tr>
  )

  const SummaryRow = () => (
    <tr className="md-x total-row light">
      <td colSpan={metadataColCount} />
      <td className="whitespace-nowrap">
        {formatNumber(totals?.orderCount ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        {formatNumber(totals?.quantity ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.gross ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.serviceFees ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.facilityFees ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.processingFees ?? 0)}
      </td>
      {showClubSeatLicenseFees && (
        <td className="whitespace-nowrap">
          {formatCurrency(totals?.clubSeatLicenseFees ?? 0)}
        </td>
      )}
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.additionalFees ?? 0)}
      </td>
      <td className="whitespace-nowrap">{formatCurrency(totals?.net ?? 0)}</td>
      <td className="whitespace-nowrap">
        {formatCurrency(totals?.serviceFeesOwed ?? 0)}
      </td>
      <td className="whitespace-nowrap">
        <strong className="text-danger">
          -{formatCurrency(refundTotalDisplay)}
        </strong>
      </td>
    </tr>
  )

  const TableHeader = () => (
    <thead>
      <tr className="md md--scroll">
        {isAllAccounts && (
          <th className="uppercase text-body text-left">Sales Rep</th>
        )}
        <th
          className="uppercase text-body text-left"
          title="Processed / completed time — same as Order Listings Order date"
        >
          Order Date
        </th>
        <th className="uppercase text-body text-left">Date Paid</th>
        <th className="uppercase text-body text-left">Order ID</th>
        <th className="uppercase text-body text-left">Customer Name</th>
        <th className="uppercase text-body text-left">Customer Email</th>
        <th className="uppercase text-body text-left">Event</th>
        <th className="uppercase text-body text-left">Event Date</th>
        <th className="uppercase text-body text-left">Product Type</th>
        <th className="uppercase text-body text-left">Quantity</th>
        <th className="uppercase text-body text-left">Gross</th>
        <th className="uppercase text-body text-left">Service Fees</th>
        <th className="uppercase text-body text-left">Facility Fee</th>
        <th className="uppercase text-body text-left">Processing Fees</th>
        {showClubSeatLicenseFees && (
          <th className="uppercase text-body text-left">
            Club Seat License Fees
          </th>
        )}
        <th className="uppercase text-body text-left">Additional Fees</th>
        <th className="uppercase text-body text-left">Net</th>
        <th className="uppercase text-body text-left">Service Fees Owed</th>
        <th className="uppercase text-body text-left">Refund</th>
        <th className="uppercase text-body text-left">Payment Method</th>
        <th className="uppercase text-body text-left">Status</th>
      </tr>
    </thead>
  )

  if (data && raw.length === 0) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>
            No transactions for this date{' '}
            {account !== 'all' && (
              <span>under the {capitalizeString(account)} account</span>
            )}
          </p>
          <p className="subtitle subtitle--dark">
            There are no transactions. Please change your filters.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  return (
    <Card body className="card--md">
      <Card.Title as="h5" className="mb-3">
        Detailed Transactions{' '}
        {!isAllAccounts && <span>for {capitalizeString(account)}</span>}
      </Card.Title>
      <div className="table-container">
        <Table responsive>
          <thead>
            <SummaryHeader />
          </thead>
          <tbody>
            <SummaryRow />
            <EmptyRow colSpan={detailColCount} />
          </tbody>
        </Table>
        <div className="table-container--fixed-height--scrollable">
          <Table responsive>
            <TableHeader />
            <tbody>
              {raw.map((order) => {
                const statusLabel = getDailySettlementStatusLabel(
                  order,
                  ORDER_STATUS_LABEL?.[order?.status] ??
                    capitalizeString(order?.status ?? EMPTY_DISPLAY_VALUE)
                )
                const refundAmount = getDailySettlementRefundAmount(order)
                return (
                  <tr
                    key={order?.orderId}
                    className={`md-x md-x--scroll${
                      isRefundedOrder(order)
                        ? ' table-danger'
                        : hasPartialRefund(order)
                          ? ' table-warning'
                          : ''
                    }`}
                  >
                    {isAllAccounts && (
                      <td className="whitespace-nowrap text-left">
                        {order?.accountRep
                          ? capitalizeString(order.accountRep)
                          : EMPTY_DISPLAY_VALUE}
                      </td>
                    )}
                    <td className="whitespace-nowrap text-left">
                      {formatOrderDate(
                        getSettlementOrderTimestamp(order),
                        order?.timezone
                      )}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {order?.datePaid
                        ? (() => {
                            const raw = order.datePaid
                            const dateOnly =
                              (typeof raw === 'string' &&
                                /^\d{4}-\d{2}-\d{2}$/.test(raw.trim())) ||
                              (raw instanceof Date &&
                                raw.getUTCHours() === 0 &&
                                raw.getUTCMinutes() === 0 &&
                                raw.getUTCSeconds() === 0)
                            return dateOnly
                              ? moment.utc(raw).format('ddd, MMM D, YYYY')
                              : moment
                                  .utc(raw)
                                  .tz(tz)
                                  .format('ddd, MMM D, YYYY')
                          })()
                        : EMPTY_DISPLAY_VALUE}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {order?.orderId ?? EMPTY_DISPLAY_VALUE}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {capitalizeString(order?.customerName?.trim()) ||
                        EMPTY_DISPLAY_VALUE}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {order?.customerEmail?.trim() || EMPTY_DISPLAY_VALUE}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {getEventNameForOrder(order)}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {getEventDateForOrder(order, order?.timezone)}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {PRODUCT_TYPE_LABEL[order?.productType] ??
                        capitalizeString(
                          order?.productType ?? EMPTY_DISPLAY_VALUE
                        )}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {formatNumber(order?.quantity ?? 0)}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>
                          -{formatCurrency(Math.abs(Number(order?.gross ?? 0)))}
                        </>
                      ) : (
                        formatCurrency(order?.gross ?? 0)
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>
                          -
                          {formatCurrency(
                            Math.abs(Number(order?.serviceFee ?? 0))
                          )}
                        </>
                      ) : (
                        formatCurrency(order?.serviceFee ?? 0)
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>-{formatCurrency(Math.abs(getFacilityFee(order)))}</>
                      ) : (
                        formatCurrency(getFacilityFee(order))
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>
                          -
                          {formatCurrency(
                            Math.abs(getSettlementProcessingFee(order))
                          )}
                        </>
                      ) : (
                        formatCurrency(getSettlementProcessingFee(order))
                      )}
                    </td>
                    {showClubSeatLicenseFees && (
                      <td
                        className={`whitespace-nowrap text-left${
                          isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                        }`}
                      >
                        {isRefundedOrder(order) ? (
                          <>
                            -
                            {formatCurrency(
                              Math.abs(getSettlementClubSeatLicenseFees(order))
                            )}
                          </>
                        ) : (
                          formatCurrency(
                            getSettlementClubSeatLicenseFees(order)
                          )
                        )}
                      </td>
                    )}
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>
                          -
                          {formatCurrency(
                            Math.abs(getSettlementOtherAdditionalFees(order))
                          )}
                        </>
                      ) : (
                        formatCurrency(getSettlementOtherAdditionalFees(order))
                      )}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        isRefundedOrder(order) ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {isRefundedOrder(order) ? (
                        <>
                          -{formatCurrency(Math.abs(getSettlementNet(order)))}
                        </>
                      ) : (
                        formatCurrency(getSettlementNet(order))
                      )}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {formatCurrency(order?.serviceFeeOwed ?? 0)}
                    </td>
                    <td
                      className={`whitespace-nowrap text-left${
                        refundAmount > 0 ? ' text-danger fw-bolder' : ''
                      }`}
                    >
                      {formatRefundCell(order)}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {formatPaymentMethodLabel(order?.paymentMethod)}
                    </td>
                    <td className="whitespace-nowrap text-left">
                      {statusLabel}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </Table>
        </div>
      </div>
    </Card>
  )
}

function Results({
  isLoading,
  data,
  fromDate,
  toDate,
  isDateRangeInvalid,
  accounts,
  account,
  displayTimezone,
  showClubSeatLicenseFees,
}) {
  const [key, setKey] = useState('method')

  if (!fromDate || isDateRangeInvalid) {
    return (
      <Card body>
        <EmptyContainer style="center lg report">
          <p>Get started by choosing your filters</p>
          <p className="subtitle subtitle--dark">
            Select an account and start date from the options above. Optionally
            choose an end date for a custom range. Once you've made your
            selections, your report will appear right away.
          </p>
        </EmptyContainer>
      </Card>
    )
  }

  if (isLoading) {
    return <LoadingContainer />
  }

  return (
    <Tabs
      defaultActiveKey="method"
      variant="pills"
      activeKey={key}
      onSelect={(k) => setKey(k)}
      className="w-max-content nav-pills-default"
    >
      <Tab eventKey="method" title="Summary by Payment Method">
        <DailySettlementByPaymentMethod
          data={data}
          account={account}
          showClubSeatLicenseFees={showClubSeatLicenseFees}
        />
      </Tab>
      <Tab eventKey="transactions" title="Detailed Transactions">
        <DailySettlementTransactions
          account={account}
          data={data}
          displayTimezone={displayTimezone}
          showClubSeatLicenseFees={showClubSeatLicenseFees}
        />
      </Tab>
    </Tabs>
  )
}
